import AbstractWizardStep from "../AbstractWizardStep.js";
import AlertCollector from "../../alerts/AlertCollector.js";
import FileUploadButtonWidget from "../../widgets/FileUploadButtonWidget.js";

/**
 * `UploadFileStep` is a step that lets the user upload a file.
 *
 * @param {Renderable} stepText - Text describing the step that will be displayed to the user
 * @param {Renderable} uploadButtonLabel - Label for the upload button
 * @param {function} spec.onUpload - Called with a file as argument. Should return a promise
 * @param {function} [spec.validate] - Callback function to test if the selected file is valid
 *   before uploading it (e.g., check for file type). It receives a file as parameter and
 *   should return a AlertCollector
 */
export default class UploadFileStep extends AbstractWizardStep {
	constructor({
		stepText,
		uploadButtonLabel,
		onUpload,
		validate = () => new AlertCollector(),
	}) {
		super(...arguments);
		this._stepText = stepText;
		this._uploadButtonLabel = uploadButtonLabel;
		this._validate = validate;

		this._onUpload = onUpload;
	}

	getRenderable() {
		return (html) => {
			html.div(
				{ klass: "data-section" },
				html.p(this._stepText),
				this._uploadButtonRenderer(),
				html.label(
					{ klass: "imported-file-name" },
					this._filename || "",
				),
			);
		};
	}

	_uploadButtonRenderer() {
		return new FileUploadButtonWidget({
			cssClass: "file-upload btn btn-primary",
			label: this._uploadButtonLabel,
			onUpload: this._onUpload,
			onSuccess: this._onSuccess.bind(this),
			onError: this._onFailure.bind(this),
			validate: this._validateFile.bind(this),
		});
	}

	_validateFile(file) {
		this._submitExceptions.reset();

		const validation = this._validate(file);

		validation
			.getErrors()
			.forEach((error) => this.addSubmitError(error.getDescription()));

		if (!this._submitExceptions.isEmpty()) {
			this.submitException.trigger();
		}

		return !validation.hasError();
	}

	_onSuccess(file, data) {
		if (data?.hasErrors || data?.typeOfError) {
			this.addSubmitError(data.message);
		} else {
			this._filename = file.name;
			this._uploadResponse = data;
		}

		this.changed.trigger();
		this.completed.trigger();
	}

	_onFailure({ error }) {
		this.addSubmitError(error.message);
		this.changed.trigger();
	}

	_isCompleted() {
		return !!this._filename;
	}

	_doStart() {
		this._filename = undefined;
		this._uploadResponse = undefined;
	}

	_doSubmit({ onDone }) {
		this._submitCallback({
			onDone,
			uploadResponse: this._uploadResponse,
			step: this,
		});
	}
}
